import { createContext, useContext, useState, useEffect } from "react";
import dayjs from "dayjs";

import environment from "../env";
import { useAuth } from "./AuthProvider";
import useSWRImmutable from "swr/immutable";

import { useAccount } from "./AccountProvider";
import { useSfs } from "./SfsProvider";

const ArrangementContext = createContext();

/**
 * Arrangement custom hook
 * */
export const useArrangement = () => useContext(ArrangementContext);

const ArrangementProvider = ({ children }) => {
  
  const { accessToken } = useAuth();

  const { useGetAccount } = useAccount();
  const { account } = useGetAccount();
  const { disposableIncome } = useSfs();

  const [ hasArrangement, setHasArrangement ] = useState(null);
  const [ plan, setPlan ] = useState(null);
  const [ lastSavedPlan, setLastSavedPlan ] = useState(null);
  const [ planMode, setPlanMode ] = useState(null);
  const [ planLoaded, setPlanLoaded ] = useState(false);

  const apiKey = environment.CUSTOMER_PORTAL_API_KEY;
  const apiUrl = environment.CUSTOMER_PORTAL_API;

  const MAX_INSTALMENTS = 250;

  const defaultOptions = {
    method: "GET",
    headers: {
      "x-api-key": apiKey,
      Authorization: `Bearer ${accessToken}`,
    },
  };

  /**
   * Post arrangement callback
   * @param {*} url
   * @param {*} arg
   * @returns
   */
  async function postArrangement(url, arrangement) {
    let requestOptions = {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(arrangement),
    };
    console.info("post arrangement request: ", { url, requestOptions });
    await fetch(`${apiUrl}/arrangement`, requestOptions)
      .then((res) => res.json())
      .then((data) => {
        console.log("post arrangement response: ", data);
      })
      .catch((error) => {
        console.log(`Error in post arrangement: ${error}`);
      });
  };

  /**
   * Get arrangement SWR hook
   * @returns
   */
  function useGetArrangement() {
    const url = accessToken ? `${apiUrl}/arrangement` : null;
    console.info("Get arrangement request", { defaultOptions, url });
    const { data, error, isLoading } = useSWRImmutable([url, defaultOptions]);
    console.info("Get arrangement response", { data, error, isLoading });
    return {
      arrangement: data,
      error,
      isArrangementLoading: isLoading,
    };
  };

  /**
   * Post Chase forbearance callback
   * @param {*} url
   * @param {*} arg
   * @returns
   */
  async function postChaseForbearance(postForbearanceRequest) {
    let requestOptions = {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(postForbearanceRequest),
    };
    const path = '/chase/forbearance';
    console.info("post chase forbearance request: ", { path, requestOptions });
    return fetch(`${apiUrl}${path}`, requestOptions).then((res) => res.json());
  };

  const paymentMethods = {
    "CASH PAYMENT": 1,
    "CHECK": 2,
    "MONEY ORDER": 3,
    "WESTERN UNION": 4,
    "DEBIT CARD PAYMENT": 5,
    "PAPER DRAFT": 6,
    "ACH DEBIT": 7,
    "POST-DATED CHECK": 8,
    "MERCHANDISE RETURN": 9,
    "RESERVED": 10,
    "UK DIRECT DEBIT": 11,
    "STANDING ORDER": 12,
    "DEBT MANAGEMENT": 13,
    "DIRECT TO BANK": 14,
    "CHEQUE PAYMENT": 15,
    "DIRECT TO CLIENT": 16,
    "PAYPOINT (BARCODE)": 17,
    "GOOGLE PAY": 18,
    "APPLE PAY": 19,
    "CHAPS": 20,
    "CASH": 21,
    "DEBIT CARD": 22,
    "UNSPECIFIED": 23,
    "OTHER": 24,
    "DEBIT DEBIT MANDATE": 25,
    "CLIENT CONTROLLED DD": 26,
    "SEPA": 28,
    "PAYPAL": 29,
    "DEBT ARR SCHEME": 30,
    "POSTAL ORDER": 31,
    "PAYMENT BOOK": 32,
  };

  const paymentFrequencies = {
    monthly: "Monthly",
    weekly: "Weekly",
    biweekly: "Fortnightly",
    every28days: "4 Weekly",
  };

  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  /**
   * Co-ordinate load of external arrangement data into state
   * @returns
   */
  const loadPlanData = (arrangement) => {
    console.log("Load Arrangement Data: ", arrangement);

    // Handle if missing arrangement
    let hasArrangement = true;
    if (arrangement === null || arrangement === "{}" || Object.keys(arrangement).length === 0) {
      hasArrangement = false;
    };

    const currentDate = dayjs();
    const TARGET_DAY = 27;
    const firstPaymentDate =
      currentDate.date() === TARGET_DAY
        ? currentDate.add(1, "month").date(TARGET_DAY)
        : currentDate.date(TARGET_DAY);

    const firstPaymentDay = firstPaymentDate.date();

    // Extract payment frequency information
    const paymentFrequency = hasArrangement ? arrangement?.paymentFrequency : "monthly";
    const paymentMonthlyDay = hasArrangement ? arrangement?.monthlyDay : firstPaymentDay;
    const paymentWeeklyDay = hasArrangement ? arrangement?.weeklyDay : "Monday";

    // Extract values needed where calculations required
    const paymentAmount = hasArrangement ? arrangement?.instalmentAmount : 0;
    const numInstalments = hasArrangement ? arrangement?.numberOfInstalments : null;
    const totalPayable = hasArrangement ? arrangement?.totalPayable : account?.arrearsAmount;

    // Calculate if plan is in direct debit payment window
    const paymentMethod = hasArrangement ? paymentMethods[arrangement?.paymentMethod] :
      paymentMethods["DIRECT TO BANK"];

    // Plan details
    const planData = {
      dateCreated: firstPaymentDate,
      instalmentAmount: paymentAmount,
      nextPaymentAmount: hasArrangement ? arrangement?.nextPaymentAmount : null,
      nextPaymentDate: hasArrangement ? arrangement?.nextPaymentDate : firstPaymentDate,
      inPaymentWindow: false,
      numberOfInstalments: numInstalments,
      paymentsLeft: hasArrangement ? arrangement?.paymentsLeft : numInstalments,
      paidToDate: hasArrangement ? arrangement?.paidToDate : 0,
      paymentFrequency: paymentFrequency,
      paymentMethod: paymentMethod,
      totalPayable: totalPayable,
      totalRemaining: hasArrangement ? arrangement?.totalRemaining : totalPayable,
      sortCode: hasArrangement ? arrangement?.sortCode : null,
      accountNumber: hasArrangement ? arrangement?.accountNumber : null,
      walletId: hasArrangement ? arrangement?.walletId : null,
      isPromiseToPay: true,
      endDate: null,
      weeklyDay: paymentWeeklyDay,
      monthlyPickOrdinal: null,
      monthlyPickDay: null,
      monthlyDay: paymentMonthlyDay,
      finalPaymentAmount: null,
      type: null,
      settlementPercentage: null,
      discount: null,
      status: null,
      isSettlement: false,
      sif: false,
    };
    console.log("PLAN DATA: ", planData);
    setPlan(planData);
    setLastSavedPlan(planData);
    setPlanMode(hasArrangement ? null : "create");
    setHasArrangement(hasArrangement ? true : false);
    setPlanLoaded(true);
  };

  useEffect(() => {
    if (account && plan && disposableIncome && disposableIncome >= 1) {
      // On DI change, update the default plan used by create arrangement feature
      if (hasArrangement === false) {
        const paymentAmount = disposableIncome <= account?.currentBalance ? disposableIncome : account?.currentBalance;
        const defaultNumInstalments = Number(Math.ceil(account?.currentBalance/disposableIncome));
        let numInstalments;
        let totalPayable;
        if (defaultNumInstalments <= MAX_INSTALMENTS) {
          numInstalments = defaultNumInstalments;
          totalPayable = account?.currentBalance;
        } else {
          numInstalments = MAX_INSTALMENTS;
          totalPayable = MAX_INSTALMENTS * paymentAmount;
        }
        setPlan({
          ...plan,
          instalmentAmount: paymentAmount,
          numberOfInstalments: numInstalments,
          totalPayable: totalPayable,
          totalRemaining: totalPayable
        });
      }
    }
  }, [disposableIncome]);

  /**
   * Co-ordinate dump of internal arrangement data into POST body
   * @returns
   */
  const savePlanData = (plan) => {
    console.log("save plan data: ", plan);

    const arrangementObj = {
      startDate: plan?.dateCreated.format("YYYY-MM-DD"),
      paymentAmount: plan?.instalmentAmount,
      totalAmount: plan?.instalmentAmount * plan?.numberOfInstalments,
      frequency: plan?.paymentFrequency,
      paymentMethod: plan?.paymentMethod,
      walletId: plan?.walletId,
      bankAccountNumber: plan?.accountNumber,
      sortCode: plan?.sortCode,
      SIF: plan?.sif,
      weeklyDay: plan?.paymentFrequency !== "monthly" ? plan?.weeklyDay : null,
      monthlyPickOrdinal: plan?.monthlyPickOrdinal,
      monthlyPickDay: plan?.monthlyPickDay,
      monthlyDay:
        plan?.paymentFrequency === "monthly" ? plan?.monthlyDay : null,
      isPromiseToPay: true,
    };

    console.log("New Arrangement POST Object: ", arrangementObj);

    if (accessToken) {
      postArrangement(`${apiUrl}/arrangement`, arrangementObj);
    }

    // Make plan the new last saved plan for dashboard widget etc
    setLastSavedPlan(plan);
  };

  const frequencyConversion = {
    monthly: "months",
    weekly: "weeks",
    biweekly: "weeks",
    every28days: "weeks",
  };

  const updatePlanByPaymentAmount = (amount) => {

    // If this amount is over DI do not allow it
    if (amount > disposableIncome) {
      return;
    }

    // How many instalments would clear the balance using the new amount?
    const totalInstalments = Number(Math.ceil(account?.currentBalance / amount));

    let planNumInstalments = null;
    let finalPaymentAmount = null;
    let planTotalAmount = null;

    if (totalInstalments <= MAX_INSTALMENTS) {
      // This amount can clear the balance without exceeding max allowed instalments
      planNumInstalments = totalInstalments;
      planTotalAmount = account?.currentBalance;
    } else {
      // This amount cannot clear the balance so cap at max instalments
      planNumInstalments = MAX_INSTALMENTS;
      planTotalAmount = amount * planNumInstalments;
    }

    // Recalculate end date using new instalment number
    switch (plan?.paymentFrequency) {
      case "monthly":
      case "weekly":
        setPlan({
          ...plan,
          instalmentAmount: amount,
          numberOfInstalments: planNumInstalments,
          paymentsLeft: planNumInstalments,
          totalPayable: planTotalAmount,
          totalRemaining: planTotalAmount,
          paidToDate: 0
        });
        break;
      case "biweekly":
        setPlan({
          ...plan,
          instalmentAmount: amount,
          numberOfInstalments: planNumInstalments,
          paymentsLeft: planNumInstalments,
          totalPayable: planTotalAmount,
          totalRemaining: planTotalAmount,
          paidToDate: 0
        });
        break;
      case "every28days":
        setPlan({
          ...plan,
          instalmentAmount: amount,
          numberOfInstalments: planNumInstalments,
          paymentsLeft: planNumInstalments,
          totalPayable: planTotalAmount,
          totalRemaining: planTotalAmount,
          paidToDate: 0,
          finalPaymentAmount: finalPaymentAmount,
        });
        break;
      default:
        break;
    }
  };

  const contextValue = {
    useGetArrangement,
    postArrangement,
    postChaseForbearance,
    loadPlanData,
    savePlanData,
    planLoaded,
    setPlanLoaded,
    planMode,
    setPlanMode,
    plan,
    setPlan,
    lastSavedPlan,
    setLastSavedPlan,
    updatePlanByPaymentAmount,
    paymentMethods,
    paymentFrequencies,
    hasArrangement,
    setHasArrangement,
    daysOfWeek,
  };

  return (
    <ArrangementContext.Provider value={contextValue}>
      {children}
    </ArrangementContext.Provider>
  );
};

export default ArrangementProvider;
